import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
// sections
import FormDialog from './FormDialog';
// components
import Breadcrumbs from '../../../components/breadcrumbs';
import { Label, ButtonField, TextField, ButtonFields, SelectField, Message as FormMessage } from '../../../components/hook-form';
import Dialog from '../../../components/dialog';
// @form
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// utils
import { REGEXP_SPECIAL } from '../../../utils/regexp';
// styles
import { Container, Message, ButtonWrapper, PrimaryButton, TertiaryButton, Switch } from '../../../styles';
// apis
import { getGroups, existsNumber, existsLocation, createDevices } from '../../../apis';

// ----------------------------------------------------------------------

const size = 10;

// ----------------------------------------------------------------------

const schema = yup.object().shape({
  number: yup.number()
    .required()
    .min(1, '1이상 정수를 입력해 주세요.')
    .integer('1이상 정수를 입력해 주세요.'),
  location: yup.string()
    .required()
    .trim()
    .min(2, '2~30자 사이 한글, 영문 대소문자, 숫자, 특수기호!@&()-_+[]{}. 를 사용해 주세요.')
    .max(30, '2~30자 사이 한글, 영문 대소문자, 숫자, 특수기호!@&()-_+[]{}. 를 사용해 주세요.')
    .matches(REGEXP_SPECIAL, '2~30자 사이 한글, 영문 대소문자, 숫자, 특수기호!@&()-_+[]{}. 를 사용해 주세요.'),
  lowBatteryLevel: yup.number()
    .min(1, '1~100 사이 정수를 입력해 주세요.')
    .max(100, '1~100 사이 정수를 입력해 주세요.')
    .integer('1~100 사이 정수를 입력해 주세요.'),
  measurementCycle: yup.string()
    .required(),
  airCompressorActionCycle: yup.string()
    .required(),
  airCompressorSprayTime: yup.string()
    .required(),
  fanControlTemperature: yup.number()
    .min(1, '1~100 사이 정수를 입력해 주세요.')
    .max(100, '1~100 사이 정수를 입력해 주세요.')
    .integer('1~100 사이 정수를 입력해 주세요.')
});

// ----------------------------------------------------------------------

interface Props {
  number?: number;
  location?: string;
  lowBatteryLevel?: number;
  measurementCycle?: string;
  airCompressorActionCycle?: string;
  airCompressorSprayTime?: string;
  fanControlTemperature?: number;
};

// ----------------------------------------------------------------------

export default function FormPage() {
  const navigate  = useNavigate();

  const {
    control,
    trigger,
    getValues,
    setFocus,
    setError,
    clearErrors,
    handleSubmit
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      number: 1,
      location: '',
      lowBatteryLevel: 1,
      measurementCycle: '5',
      airCompressorActionCycle: '2',
      airCompressorSprayTime: '20',
      fanControlTemperature: 1
    }
  });
  
  const [ data, setData ] = useState({ content: [], totalElements: 0, totalPages: 1 });
  const { content: rows, totalElements: total, totalPages: pages } = data;

  const [ keyword, setKeyword ] = useState('');
  const [ page, setPage ] = useState(0);

  const [ groups, setGroups ] = useState({id: null, name: ''});
  const [ errors, setErrors ] = useState(null);

  const [ visible, setVisible ] = useState(false);

  useEffect(() => {
    handleSearch();
  }, []);

  useEffect(() => {
    if ( errors !== null ) {
      if ( groups.id ) return setErrors(false);
      setErrors(true);
    }
  }, [groups]);

  const handlePage = (value: number) => {
    setPage(value-1);
    handleSearch({page: value-1, size});
  };

  const handleSearch = (paging?: { page: number, size: number }) => {
    if ( keyword.length > 0 && keyword.replace(/ /g, '').length === 0 ) return;

    if ( !paging ) setPage(0);
    getGroups(
      setData, keyword,
      paging ? { page: paging.page, size: paging.size } : { page: 0, size }
    );
  };

  const [ checkNumber, setCheckNumber ] = useState({result: null, message: null});
  const [ checkedNumber, setCheckedNumber ] = useState(null);

  const [ checkLocation, setCheckLocation ] = useState({result: null, message: null});
  const [ checkedLocation, setCheckedLocation ] = useState(null);

  const [ message, setMessage ] = useState(null);

  const handleNumber = async () => {
    const number = getValues('number');
    if ( number < 1 || number % 1 !== 0 ) {
      setError('number', {type: 'integer', message: '1이상 정수를 입력해 주세요.'});
      setFocus('number');
    } else {
      clearErrors('number');
      await existsNumber(groups.id, number)
        .then((response) => {
          setCheckNumber({result: response, message: response ? '이미 등록된 장비 번호입니다.' : '사용하실 수 있는 장비 번호입니다.'});
          setCheckedNumber(number);
        })
        .catch(() => {
          setCheckNumber({result: null, message: '나중에 다시 시도해 주세요.'});
        });
    }
  };

  const handleLocation = async () => {
    const location = getValues('location').trim();
    if ( location === '' ) {
      setError('location', {type: 'required'});
      setFocus('location');
    } else if ( location.length < 2 || location.length > 30 || !REGEXP_SPECIAL.test(location) ) {
      setError('location', {type: 'matches', message: '국/영문 대소문자, 숫자, 2~30자 이내, 특수기호!@&()-_+[]{}.를 사용해 주세요.'});
      setFocus('location');
    } else {
      clearErrors('location');
      await existsLocation(groups.id, location)
        .then((response) => {
          setCheckLocation({result: response, message: response ? '이미 등록된 설치 위치입니다.' : '사용하실 수 있는 설치 위치입니다.'});
          setCheckedLocation(location);
        })
        .catch(() => {
          setCheckLocation({result: null, message: '나중에 다시 시도해 주세요.'});
        });
    }
  };

  const [ power, setPower ] = useState('COMMERCIAL');
  const [ airCompressorEnable, setAirCompressorEnable ] = useState(true);
  const [ fanEnable, setFanEnable ] = useState(true);

  useEffect(() => {
    if ( power === 'COMMERCIAL' ) {
      clearErrors('lowBatteryLevel');
    } else {
      trigger('lowBatteryLevel');
    }
  }, [power]);

  useEffect(() => {
    if ( !airCompressorEnable ) {
      clearErrors('airCompressorActionCycle');
      clearErrors('airCompressorSprayTime');
    } else {
      trigger('airCompressorActionCycle');
      trigger('airCompressorSprayTime');
    }
  }, [airCompressorEnable]);

  useEffect(() => {
    if ( !fanEnable ) {
      clearErrors('fanControlTemperature');
    } else {
      trigger('fanControlTemperature');
    }
  }, [fanEnable]);

  const [ success, setSuccess ] = useState({result: false, answer: null });
  const handleClose = () => {
    setSuccess({result: false, answer: null });
    navigate('/iot/devices', {state: {submit: true}});
  };
  const handleApply = () => {
    setSuccess({result: false, answer: null });
    navigate('/iot/devices/sensors', {state: {submit: true, answer: success.answer}});
  };

  const onSubmit = async (data: Props) => {
    if ( checkNumber.result === null || Number(checkedNumber) !== data.number ) {
      setCheckNumber({result: null, message: '중복 체크 후 진행하실 수 있습니다.'});
    }

    if ( checkLocation.result === null || checkedLocation !== data.location ) {
      setCheckLocation({result: null, message: '중복 체크 후 진행하실 수 있습니다.'});
    }

    if ( !groups.id ) setErrors(true);

    if ( groups.id && checkNumber.result === false && Number(checkedNumber) === data.number && checkLocation.result === false && checkedLocation === data.location ) {
      const lowBatteryLevel = power === 'COMMERCIAL' ? null : data.lowBatteryLevel;
      const airCompressorActionCycle = airCompressorEnable ? data.airCompressorActionCycle : null;
      const airCompressorSprayTime = airCompressorEnable ? data.airCompressorSprayTime : null;
      const fanControlTemperature = fanEnable ? data.fanControlTemperature : null;

      await createDevices(groups.id, data.number, data.location, power, lowBatteryLevel, data.measurementCycle, airCompressorEnable, airCompressorActionCycle, airCompressorSprayTime, fanEnable, fanControlTemperature)
        .then((response) => {
          setSuccess({result: true, answer: { iotDeviceId: response, iotGroupId: groups.id, iotGroupName: groups.name, location: data.location }});
        })
        .catch(() => setMessage('요청하신 수질 센서를 등록 할 수 없습니다.\n나중에 다시 시도해 주세요.'));
    }
  };

  const onError = () => {
    if ( !groups.id ) setErrors(true);
  };

  return (
    <Container>
      <Breadcrumbs
        heading='IoT 장비 등록'
        links={[
          {name: 'IoT 장비 관리'},
          {name: 'IoT 장비', link: '/iot/devices'},
          {name: 'IoT 장비 등록'}
        ]}
      />

      <form onSubmit={ handleSubmit(onSubmit, onError) } noValidate>

        <div>
          <Label><span>* </span>IoT 장비 그룹</Label>
          <ButtonFields $error={errors !== null ? errors : false}>
            <input type='text' value={groups.name} placeholder='IoT 장비 그룹을 선택해 주세요.' disabled />
            <TertiaryButton type='button' onClick={() => setVisible(true)}>검색</TertiaryButton>
          </ButtonFields>
          { errors === true &&
            <FormMessage>IoT 장비 그룹을 선택해 주세요.</FormMessage>
          }
        </div>

        <ButtonField
          type='number'
          name='number'
          label='장비 번호'
          essential={true}
          disabled={groups.id ? false : true}
          control={control}
          exists={checkNumber}
          action={
            <TertiaryButton type='button' disabled={groups.id ? false : true} onClick={() => handleNumber()}>중복 체크</TertiaryButton>
          }
        />

        <ButtonField
          name='location'
          label='설치 위치'
          essential={true}
          type='text'
          disabled={groups.id ? false : true}
          control={control}
          exists={checkLocation}
          action={
            <TertiaryButton type='button' disabled={groups.id ? false : true} onClick={() => handleLocation()}>중복 체크</TertiaryButton>
          }
          maxLength={30}
        />

        <div>
          <Label><span>* </span>전원</Label>
          <select value={power} onChange={(event) => setPower(event.target.value)}>
            <option value='COMMERCIAL'>상시 전원</option>
            <option value='BATTERY'>배터리</option>
          </select>
        </div>
        
        <TextField
          type='number'
          name='lowBatteryLevel'
          control={control}
          label='배터리 부족 경고 (%)'
          min={1}
          max={100}
          disabled={power === 'COMMERCIAL' ? true : false}
        />

        <SelectField
          name='measurementCycle'
          control={control}
          label='데이터 측정 주기 (분)'
          essential={true}
          option={[
            {value: '5', name: '5'},
            {value: '10', name: '10'},
            {value: '15', name: '15'},
            {value: '20', name: '20'}
          ]}
        />

        <Switch>
          <Label><span>* </span>에어 컴프레서 활성화</Label>
          <label>
            <input type='checkbox' checked={airCompressorEnable} onChange={(event) => setAirCompressorEnable(event.target.checked)} />
          </label>
        </Switch>

        <SelectField
          name='airCompressorActionCycle'
          control={control}
          label='에어 컴프레서 동작 주기 (시)'
          option={[
            {value: '2', name: '2'},
            {value: '4', name: '4'},
            {value: '6', name: '6'},
            {value: '8', name: '8'},
            {value: '12', name: '12'}
          ]}
          disabled={!airCompressorEnable ? true : false}
        />

        <SelectField
          name='airCompressorSprayTime'
          control={control}
          label='에어 컴프레서 분사 주기 (초)'
          option={[
            {value: '20', name: '20'},
            {value: '30', name: '30'},
            {value: '40', name: '40'},
            {value: '50', name: '50'}
          ]}
          disabled={!airCompressorEnable ? true : false}
        />

        <Switch>
          <Label><span>* </span>환풍기 활성화</Label>
          <label>
            <input type='checkbox' checked={fanEnable} onChange={(event) => setFanEnable(event.target.checked)} />
          </label>
        </Switch>
        
        <TextField
          type='number'
          name='fanControlTemperature'
          control={control}
          label='환풍기 제어 온도 (℃)'
          min={1}
          max={100}
          disabled={!fanEnable ? true : false}
        />

        <Message $visible={message ? true : false}>{ message }</Message>

        <ButtonWrapper>
          <PrimaryButton type='submit'>등록</PrimaryButton>
          <TertiaryButton type='button' onClick={() => navigate('/iot/devices')}>취소</TertiaryButton>
        </ButtonWrapper>
      </form>

      { visible &&
        <FormDialog
          rows={rows}
          setName={setKeyword}
          page={page}
          pages={pages}
          size={size}
          handlePage={handlePage}
          handleSearch={handleSearch}
          setVisible={setVisible}
          groups={groups}
          setGroups={setGroups}
        />
      }

      { success.result &&
        <Dialog
          title='수질 센서 등록'
          handleClose={handleClose}
        >
          <p>IoT 장비 등록이 완료되었습니다.<br />수질 센서도 등록하시겠습니까?</p>

          <ButtonWrapper style={{'marginTop': '30px'}}>
            <PrimaryButton onClick={() => handleApply()}>확인</PrimaryButton>
            <TertiaryButton type='button' onClick={() => handleClose()}>취소</TertiaryButton>
          </ButtonWrapper>
        </Dialog>
      }
      
    </Container>
  );
}